import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Checkbox, IllustrativeIcon, StandAloneLink, Surface, Txt } from '@vst/beam';
import { Alert } from '@vst/beam-icons/illustrative-icons';
import { SpacingTypes } from '@vst/beam/src/react/common';
import { useTranslation } from 'react-i18next';

import { Container } from '@app/components/Container';
import { useScreenResolution } from '@app/hooks';
import { ChangePlanSteps, setChangePlanStep } from '@app/redux';

import styles from './OptionalServiceWarning.module.scss';

export const CardContent = ({
  handleBack,
  services,
  serviceWarning,
  description,
  consentLabel,
}: {
  handleBack: (e: React.MouseEvent<HTMLElement>) => void;
  services: React.ReactNode;
  serviceWarning: string;
  description: React.ReactNode;
  consentLabel: string;
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ChangePlan', { keyPrefix: 'droppedAddOns' });

  const { isLarge, isExtraLarge, isMedium, isSmall, isExtraSmall } = useScreenResolution();
  const isMobile = !isLarge && !isExtraLarge && !isMedium;

  const marginVertical: SpacingTypes = isMobile ? '16px' : '40px';
  const marginHorizontal: SpacingTypes | undefined = isSmall || isExtraSmall ? undefined : '32px';

  const [hasConsented, setHasConsented] = useState(false);

  return (
    <Container mt={marginVertical} mb={marginVertical} mx={marginHorizontal}>
      <Surface style={{ background: 'transparent' }}>
        <Surface className={styles['body']}>
          <Surface className={styles['title']}>
            <IllustrativeIcon style={{ alignSelf: 'center' }} icon={Alert} color="teal_600" size="88px" />
            <Txt variant="heading4" style={{ textAlign: 'center' }}>
              {t('title')}
            </Txt>
          </Surface>

          <Surface variant="secondary" radius="8px" p="24px" width="100%">
            <Txt variant="bodySmallRegular" color="subtle">
              {serviceWarning}
            </Txt>
            {services}
          </Surface>

          {description && <Surface>{description}</Surface>}

          <hr className={styles['hr']} />
          <Checkbox
            id="optionalServicesCheckbox"
            data-cy="optionalServicesCheckbox"
            label={consentLabel}
            onChange={() => setHasConsented(!hasConsented)}
          />
        </Surface>
        <Surface className={styles['footer']}>
          <StandAloneLink
            data-cy="optionalServicesBackToPlans"
            linkSize="medium"
            onClick={handleBack}
            href="#"
            variant="primary"
          >
            {t('back')}
          </StandAloneLink>
          <Button
            fluid={isMobile || isMedium}
            data-cy="optionalServicesContinue"
            disabled={!hasConsented}
            onClick={() => {
              dispatch(setChangePlanStep(ChangePlanSteps.ORDER_REVIEW));
            }}
          >
            {t('continue')}
          </Button>
        </Surface>
      </Surface>
    </Container>
  );
};
